import type { GetMessagesQueryVariables } from '#src/graphql/generated';

import { NetworkStatus } from '@apollo/client';
import { useQuery, useSubscription } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { DOCUMENT_MESSAGES_UPDATED } from '#src/graphql/subscriptions/document-messages-updated';
import { useThreadFocusSearchParams } from '#src/hooks/use-thread-focus-search-params';
import { useCurrentUser } from '#src/providers/user-provider';
import { resolveFocusParamToRootId } from '#src/utils/message-thread-focus';

export interface UseDocumentScopedMessagesOptions {
  /** When false, skips the messages query (e.g. drawer closed). */
  enabled: boolean;
}

export function useDocumentScopedMessages(
  documentId: string | undefined,
  { enabled }: UseDocumentScopedMessagesOptions,
) {
  const user = useCurrentUser();
  const {
    rawFocusMessageId,
    expandThread,
    collapseThread,
    replaceStripFocusParam,
  } = useThreadFocusSearchParams();

  const queryEnabled =
    Boolean(documentId) && enabled && Boolean(user.email?.trim());

  const getMessagesVariables = useMemo<GetMessagesQueryVariables>(
    () => ({
      input: {
        contextFilters: [{ name: 'documentId', value: documentId ?? '' }],
      },
    }),
    [documentId],
  );

  const {
    data: messagesData,
    error: rawMessagesError,
    networkStatus: messagesNetworkStatus,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: getMessagesVariables,
    fetchPolicy: 'cache-and-network',
    skip: !queryEnabled,
  });

  useSubscription(DOCUMENT_MESSAGES_UPDATED, {
    variables: { documentId: documentId ?? '' },
    skip: !queryEnabled || !documentId,
    onData: () => {
      void refetchMessages();
    },
  });

  const messagesError = queryEnabled ? rawMessagesError : undefined;

  const isMessagesInitialLoad =
    messagesNetworkStatus === NetworkStatus.loading && messagesData == null;

  const sortedMessages = useMemo(
    () =>
      [...(messagesData?.messages || [])].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [messagesData?.messages],
  );

  const focusedRootId = useMemo(
    () => resolveFocusParamToRootId(rawFocusMessageId, sortedMessages),
    [rawFocusMessageId, sortedMessages],
  );

  const displayMessages = useMemo(
    () =>
      focusedRootId
        ? sortedMessages.filter((m) => m.id === focusedRootId)
        : sortedMessages,
    [sortedMessages, focusedRootId],
  );

  useEffect(() => {
    if (!queryEnabled || isMessagesInitialLoad) return;
    if (rawFocusMessageId && !focusedRootId) {
      replaceStripFocusParam();
    }
  }, [
    queryEnabled,
    isMessagesInitialLoad,
    rawFocusMessageId,
    focusedRootId,
    replaceStripFocusParam,
  ]);

  return {
    sortedMessages,
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    expandThread,
    collapseThread,
    messagesFeatureActive: queryEnabled,
  };
}
